import { useEffect, useState } from "react";
import { Plus, Trash, PencilSimple, Sparkle, X, Copy, EnvelopeSimple, ChatCircle, WhatsappLogo, Package } from "@phosphor-icons/react";
import client from "../lib/api";
import { toast } from "sonner";
import PageHeader from "./PageHeader";
import StatusPill from "./StatusPill";

/**
 * CrudModule — reusable table/list module with add, edit, delete, and AI draft.
 *
 * props:
 *  - resource: string (endpoint under /api/, e.g., "loads")
 *  - overline, title, subtitle
 *  - fields: [{ key, label, type: 'text'|'number'|'date'|'select'|'email', options?, required?, span? (1|2), placeholder? }]
 *  - columns: [{ key, label, render? }]  // table columns
 *  - aiCategory: category key for /ai/generate (optional)
 *  - aiContextFields: array of field keys to pass as context
 *  - primaryField: key for the "title" of each row (for empty state and label)
 *  - initialForm: default values for new item
 *  - extraRowActions: (row) => Array<{ icon, onClick, tooltip, testid, color?: 'primary'|'info'|'neutral' }>
 */
export default function CrudModule({
  resource, overline, title, subtitle,
  fields, columns, aiCategory, aiContextFields = [], primaryField, initialForm = {}, testidPrefix,
  extraRowActions, headerAction, renderFormExtras,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {mode, item}
  const [aiFor, setAiFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`/${resource}`);
      setItems(data);
    } catch (e) {
      toast.error(`Failed to load ${resource}`);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resource]);

  const save = async (form, id) => {
    try {
      if (id) await client.patch(`/${resource}/${id}`, form);
      else await client.post(`/${resource}`, form);
      toast.success(id ? "Updated" : "Created");
      setModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await client.delete(`/${resource}/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]" data-testid={`${testidPrefix}-page`}>
      <PageHeader
        overline={overline}
        title={title}
        subtitle={subtitle}
        action={
          <div className="flex items-center gap-2">
            {headerAction}
            <button
              data-testid={`${testidPrefix}-add-btn`}
              onClick={() => setModal({ mode: "create", item: { ...initialForm } })}
              className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2"
            >
              <Plus size={16} weight="bold" /> New
            </button>
          </div>
        }
      />

      <div className="mt-8 card-surface overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-neutral-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-white/[0.03] border border-white/10 flex items-center justify-center mx-auto mb-5">
              <Package size={26} weight="regular" className="text-neutral-500" />
            </div>
            <div className="overline mb-2">NOTHING HERE YET</div>
            <h4 className="font-display text-2xl font-semibold">Start your first {title.split(" ")[0].toLowerCase()}</h4>
            <p className="text-neutral-400 mt-3 text-sm max-w-md mx-auto leading-relaxed">
              Click <span className="text-white font-medium">New</span> above to add your first record, or head back to the dashboard and load our sample data to see how everything works.
            </p>
            <button
              data-testid={`${testidPrefix}-empty-add-btn`}
              onClick={() => setModal({ mode: "create", item: { ...initialForm } })}
              className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2 mt-6"
            >
              <Plus size={14} weight="bold" /> Add first {title.split(" ")[0].toLowerCase()}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto sm:overflow-visible relative">
            {/* Mobile scroll hint — subtle gradient on the right edge signals more content */}
            <div className="sm:hidden absolute top-0 right-0 bottom-0 w-8 bg-gradient-to-l from-[#0f1116] to-transparent pointer-events-none z-10" />
            <table className="w-full text-sm min-w-[560px] sm:min-w-0" data-testid={`${testidPrefix}-table`}>
              <thead>
                <tr className="text-left border-b border-white/10">
                  {columns.map((c) => (
                    <th key={c.key} className="overline px-2 py-3 sm:p-4 font-medium">{c.label}</th>
                  ))}
                  <th className="overline px-2 py-3 sm:p-4 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`${testidPrefix}-row-${row.id}`}>
                    {columns.map((c) => (
                      <td key={c.key} className="px-2 py-3 sm:p-4 text-neutral-200">
                        {c.render ? c.render(row) :
                          c.key === "status" ? <StatusPill status={row[c.key]} /> :
                          <span>{row[c.key] || <span className="text-neutral-600">—</span>}</span>}
                      </td>
                    ))}
                    <td className="px-2 py-3 sm:p-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        {extraRowActions && extraRowActions(row).map((a, idx) => (
                          <button
                            key={idx}
                            data-testid={a.testid}
                            onClick={a.onClick}
                            title={a.tooltip}
                            className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
                              a.color === "primary" ? "hover:bg-[#FF5A1F]/15 text-[#FF7038]" :
                              a.color === "info" ? "hover:bg-[#007AFF]/15 text-[#60a5fa]" :
                              a.color === "emerald" ? "hover:bg-emerald-500/15 text-emerald-400" :
                              "hover:bg-white/5 text-neutral-400"
                            }`}
                          >
                            {a.icon}
                          </button>
                        ))}
                        {aiCategory && (
                          <button
                            data-testid={`${testidPrefix}-ai-${row.id}`}
                            onClick={() => setAiFor(row)}
                            title="Draft with AI"
                            className="w-8 h-8 rounded-md hover:bg-[#007AFF]/15 text-[#60a5fa] flex items-center justify-center transition-colors"
                          >
                            <Sparkle size={16} weight="fill" />
                          </button>
                        )}
                        <button
                          data-testid={`${testidPrefix}-edit-${row.id}`}
                          onClick={() => setModal({ mode: "edit", item: row })}
                          className="w-8 h-8 rounded-md hover:bg-white/5 text-neutral-400 flex items-center justify-center transition-colors"
                        >
                          <PencilSimple size={15} />
                        </button>
                        <button
                          data-testid={`${testidPrefix}-delete-${row.id}`}
                          onClick={() => remove(row.id)}
                          className="w-8 h-8 rounded-md hover:bg-red-500/15 text-neutral-500 hover:text-red-400 flex items-center justify-center transition-colors"
                        >
                          <Trash size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <FormModal
          fields={fields}
          initial={modal.item}
          onCancel={() => setModal(null)}
          onSave={(form) => save(form, modal.mode === "edit" ? modal.item.id : null)}
          title={modal.mode === "edit" ? `Edit ${title}` : `New ${title}`}
          testidPrefix={testidPrefix}
          renderFormExtras={renderFormExtras}
        />
      )}

      {aiFor && (
        <AIDraftModal
          row={aiFor}
          category={aiCategory}
          contextFields={aiContextFields}
          onClose={() => setAiFor(null)}
          testidPrefix={testidPrefix}
        />
      )}
    </div>
  );
}

function FormModal({ fields, initial, onCancel, onSave, title, testidPrefix, renderFormExtras }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onCancel} data-testid={`${testidPrefix}-form-modal`}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-[#0F0F0F] border border-white/10 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h3 className="font-display text-xl font-semibold">{title}</h3>
          <button type="button" onClick={onCancel} className="text-neutral-500 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-6 max-h-[70vh] overflow-y-auto scrollbar-thin grid grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.key} className={f.span === 2 ? "col-span-2" : "col-span-2 md:col-span-1"}>
              <div className="overline mb-2">{f.label}{f.required && <span className="text-[#FF5A1F]"> *</span>}</div>
              {f.type === "select" ? (
                <select
                  data-testid={`${testidPrefix}-field-${f.key}`}
                  required={f.required}
                  value={form[f.key] ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="w-full bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white"
                >
                  {f.options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : f.type === "textarea" ? (
                <textarea
                  data-testid={`${testidPrefix}-field-${f.key}`}
                  required={f.required}
                  value={form[f.key] ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="w-full bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white min-h-[90px]"
                  placeholder={f.placeholder}
                />
              ) : (
                <input
                  data-testid={`${testidPrefix}-field-${f.key}`}
                  type={f.type || "text"}
                  required={f.required}
                  value={form[f.key] ?? ""}
                  onChange={(e) => set(f.key, f.type === "number" ? parseFloat(e.target.value || 0) : e.target.value)}
                  className="w-full bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white"
                  placeholder={f.placeholder}
                />
              )}
            </div>
          ))}
          {renderFormExtras && (
            <div className="col-span-2">{renderFormExtras(form)}</div>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10 bg-[#0A0A0A]">
          <button type="button" onClick={onCancel} data-testid={`${testidPrefix}-form-cancel`} className="px-4 py-2 text-sm text-neutral-400 hover:text-white">Cancel</button>
          <button type="submit" data-testid={`${testidPrefix}-form-save`} className="btn-primary rounded-full px-5 py-2 text-sm">Save</button>
        </div>
      </form>
    </div>
  );
}

function AIDraftModal({ row, category, contextFields, onClose, testidPrefix }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [instruction, setInstruction] = useState("");

  const generate = async (extra = "") => {
    setLoading(true);
    try {
      const context = {};
      contextFields.forEach((k) => { if (row[k] !== undefined) context[k] = row[k]; });
      const { data } = await client.post("/ai/generate", { category, context, instruction: extra });
      setText(data.text);
    } catch (e) {
      toast.error("AI draft failed");
      setText("Could not generate. Please try again.");
    } finally { setLoading(false); }
  };

  useEffect(() => { generate(); /* eslint-disable-next-line */ }, []);

  const copy = () => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const encoded = encodeURIComponent(text || "");
  const subjectMap = {
    dispatch_notification: "Dispatch Assignment",
    customer_update: "Shipment Update",
    invoice_reminder: `Payment Reminder — Invoice ${row.invoice_number || ""}`.trim(),
    driver_message: "A quick note from dispatch",
    maintenance_reminder: `Maintenance Reminder — Truck ${row.truck_number || ""}`.trim(),
    recruiting_outreach: "Driving opportunity with our fleet",
    lead_followup: "Following up on freight",
    custom: "Message",
  };
  const subject = encodeURIComponent(subjectMap[category] || "Message");
  const emailTo = row.email || row.customer_email || "";
  const smsTo = row.phone || "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid={`${testidPrefix}-ai-modal`}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-[#0F0F0F] border border-white/10 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#007AFF]/15 border border-[#007AFF]/30 flex items-center justify-center">
              <Sparkle size={18} weight="fill" color="#007AFF" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">AI Draft</div>
              <div className="text-xs text-neutral-500">Category: {category.replace(/_/g, " ")}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-6">
          <div className="min-h-[220px] p-4 rounded-md bg-[#141414] border border-white/10 text-sm text-neutral-200 whitespace-pre-wrap leading-relaxed" data-testid={`${testidPrefix}-ai-output`}>
            {loading ? <span className="stream-cursor">Drafting</span> : text}
          </div>

          <div className="mt-4 flex items-center gap-2">
            <input
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="Tweak this draft (e.g., more urgent, shorter)..."
              className="flex-1 bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-sm"
              disabled={loading}
              data-testid={`${testidPrefix}-ai-tweak-input`}
            />
            <button
              onClick={() => generate(instruction)}
              disabled={loading}
              className="px-4 py-2.5 rounded-md bg-[#007AFF]/15 border border-[#007AFF]/30 text-[#60a5fa] text-sm hover:bg-[#007AFF]/25 transition-colors disabled:opacity-50"
              data-testid={`${testidPrefix}-ai-regen-btn`}
            >
              Regenerate
            </button>
          </div>

          {!loading && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="overline mr-1">SEND VIA</span>
              <a
                href={`mailto:${emailTo}?subject=${subject}&body=${encoded}`}
                data-testid={`${testidPrefix}-ai-mailto`}
                className="px-3 py-2 rounded-md bg-[#141414] border border-white/10 hover:border-white/25 text-neutral-200 inline-flex items-center gap-2 transition-colors"
              >
                <EnvelopeSimple size={14} /> Email
              </a>
              <a
                href={`sms:${smsTo}?body=${encoded}`}
                data-testid={`${testidPrefix}-ai-sms`}
                className="px-3 py-2 rounded-md bg-[#141414] border border-white/10 hover:border-white/25 text-neutral-200 inline-flex items-center gap-2 transition-colors"
              >
                <ChatCircle size={14} /> SMS
              </a>
              <a
                href={`https://wa.me/${(smsTo || "").replace(/\D/g, "")}?text=${encoded}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`${testidPrefix}-ai-whatsapp`}
                className="px-3 py-2 rounded-md bg-[#141414] border border-white/10 hover:border-white/25 text-neutral-200 inline-flex items-center gap-2 transition-colors"
              >
                <WhatsappLogo size={14} /> WhatsApp
              </a>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10 bg-[#0A0A0A]">
          <button onClick={onClose} className="px-4 py-2 text-sm text-neutral-400 hover:text-white">Close</button>
          <button onClick={copy} disabled={loading} data-testid={`${testidPrefix}-ai-copy-btn`} className="btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50">
            <Copy size={14} weight="bold" /> Copy text
          </button>
        </div>
      </div>
    </div>
  );
}
